import React from 'react';
import { Printer, X } from 'lucide-react';
import { styles, Modal } from '../theme';
import { formatCurrency } from '../utils/formatUtils';
import { formatPHDatetime } from '../utils/dateUtils';

const formatPeso = (v) => `₱${formatCurrency(v)}`;
const formatDateTime = (v) => v ? formatPHDatetime(v) : '-';

/**
 * On-screen digital receipt (narrow, thermal-proportioned) with a Print
 * button. Chosen over a jsPDF 80mm template since most POS sessions here
 * are mobile-first and won't have a thermal printer attached — this works
 * everywhere and still prints cleanly (via the .pos-receipt-print CSS rule)
 * on a branch that does have one.
 */
const PosReceiptView = ({ sale, onClose }) => {
  if (!sale) return null;

  return (
    <Modal isOpen={!!sale} onClose={onClose} title="Receipt" maxWidth="380px">
      <div className="pos-receipt-print" style={{
        fontFamily: 'monospace',
        fontSize: '13px',
        padding: '8px 4px',
        color: '#111827'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '12px' }}>
          <div style={{ fontWeight: 700, fontSize: '15px' }}>{sale.branch_name}</div>
          <div>{formatDateTime(sale.created_at)}</div>
          <div>Receipt: {sale.receipt_number}</div>
          <div>Operator: {sale.cashier_operator_name}</div>
        </div>

        <div style={{ borderTop: '1px dashed #9ca3af', borderBottom: '1px dashed #9ca3af', padding: '8px 0', margin: '8px 0' }}>
          {(sale.items || []).map((line, idx) => (
            <div key={idx} style={{ marginBottom: '6px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{line.item_name}</span>
                <span>{formatPeso(line.line_total)}</span>
              </div>
              <div style={{ color: '#6b7280', fontSize: '12px' }}>
                {line.quantity} {line.sold_by === 'weight' ? 'kg' : 'x'} @ {formatPeso(line.unit_price)}
                {line.line_discount_amount > 0 ? ` (- ${formatPeso(line.line_discount_amount)} ${line.line_discount_name || 'discount'})` : ''}
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Subtotal</span><span>{formatPeso(sale.subtotal)}</span>
        </div>
        {sale.discount_total > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Discount{sale.sale_discount_name ? ` (${sale.sale_discount_name})` : ''}</span>
            <span>-{formatPeso(sale.discount_total)}</span>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '15px', marginTop: '6px' }}>
          <span>TOTAL</span><span>{formatPeso(sale.grand_total)}</span>
        </div>

        <div style={{ marginTop: '10px', borderTop: '1px dashed #9ca3af', paddingTop: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Payment</span><span>{sale.payment_method_name || 'Unknown'}</span>
          </div>
          {sale.payment_method_is_cash && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Tendered</span><span>{formatPeso(sale.amount_tendered)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Change</span><span>{formatPeso(sale.change_amount)}</span>
              </div>
            </>
          )}
        </div>

        {sale.status === 'voided' && (
          <div style={{ marginTop: '10px', textAlign: 'center', color: '#b91c1c', fontWeight: 700 }}>
            VOIDED{sale.void_reason ? ` — ${sale.void_reason}` : ''}
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: '14px', color: '#6b7280' }}>Thank you!</div>
      </div>

      <div style={{ marginTop: '20px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={{ ...styles.button, ...styles.buttonSecondary }}>
          <X size={16} /> Close
        </button>
        <button onClick={() => window.print()} style={styles.button}>
          <Printer size={16} /> Print
        </button>
      </div>
    </Modal>
  );
};

export default PosReceiptView;
